import { useEffect, useMemo, useState, useRef } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { TrendingUp, BarChart, GppBad } from "@mui/icons-material";
import { supabase } from "../lib/supabaseClient";
import PostComponent from "../components/dashboardComponents/postComponent";
import PostCard from "../components/dashboardComponents/PostCard";
import DashboardLayout from "../components/DashboardLayout"; // Import the Layout

const BUCKET = "report-files";

// =============================================
// MOCK DATA TOGGLE - Set to false to disable
// =============================================
const MOCK_DATA_ENABLED = false;

// Mock data generator
const generateMockData = () => {
  return [
    {
      title: "Cross-Site Scripting in User Profile",
      author: "Test User",
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      category: "XSS",
      file_path: "mock-xss-1",
      org_name: "OpenAI",
      summary: "Found a persistent XSS vulnerability in the user bio section.",
      id: "mock-1",
      is_anonymous: false,
    },
    {
      title: "SQL Injection in Login Form",
      author: "Anonymous",
      created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      category: "SQL Injection",
      file_path: "mock-sql-1",
      org_name: "Google",
      summary: "Blind SQL injection possible in the password recovery flow.",
      id: "mock-2",
      is_anonymous: true,
    },
    {
      title: "CSRF Token Bypass",
      author: "Security Researcher",
      created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      category: "CSRF",
      file_path: "mock-csrf-1",
      org_name: "Anthropic",
      summary: "CSRF protection missing on critical state-changing endpoints.",
      id: "mock-3",
      is_anonymous: false,
    },
    {
      title: "Authentication Bypass via Direct URL",
      author: "Admin User",
      created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      category: "Authentication Bypass",
      file_path: "mock-auth-1",
      org_name: "Microsoft",
      summary: "Admin panel accessible without authentication via direct link.",
      id: "mock-4",
      is_anonymous: false,
    },
    {
      title: "Insecure Deserialization in API",
      author: "Anonymous",
      created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      category: "Insecure Deserialization",
      file_path: "mock-deser-1",
      org_name: "Amazon",
      summary:
        "Java object deserialization vulnerability in the reporting API.",
      id: "mock-5",
      is_anonymous: true,
    },
  ];
};

// Cache to store dashboard data
const dashboardCache = {
  posts: null,
  stats: null,
  lastFetch: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes cache duration
};

// Internal component that receives 'user' from DashboardLayout
const DashboardContent = ({ user }) => {
  // Derived state
  const isGuest = !user;

  // Right column cards & viewer
  const [posts, setPosts] = useState([]);
  const [modelStats, setModelStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProvider, setSelectedProvider] = useState("All");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const hasFetchedRef = useRef(false);

  // Memoize date formatter to avoid recreation for each post
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  function resolvePublicUrl(file_path) {
    if (!file_path || file_path === "EMPTY" || file_path.startsWith("mock-"))
      return null;
    const normalized = String(file_path)
      .replace(/^report-files\//, "")
      .replace(/^\/+/, "");
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(normalized);
    return data?.publicUrl || null;
  }

  function normalizeStoragePath(file_path) {
    return String(file_path)
      .replace(/^report-files\//, "")
      .replace(/^\/+/, "");
  }

  // Posts Fetching Logic
  useEffect(() => {
    let alive = true;
    const fetchPosts = async (forceRefresh = false) => {
      try {
        // Set loading only for initial fetch, not cache hits
        if (!dashboardCache.posts) {
          setLoading(true);
        }

        const now = Date.now();
        const isCacheValid =
          dashboardCache.posts &&
          dashboardCache.lastFetch &&
          now - dashboardCache.lastFetch < dashboardCache.CACHE_DURATION;

        if (!forceRefresh && isCacheValid) {
          if (!alive) return;
          setPosts(dashboardCache.posts);
          setModelStats(dashboardCache.stats || []);
          const recent = dashboardCache.posts.slice(0, 5);
          setTrendingTopics([...new Set(recent.map((p) => p.category))]);
          setLoading(false);
          return;
        }

        // Parallel fetch: feed and stats
        const [feedResult, reportsResult] = await Promise.all([
          supabase
            .from("dashboard_feed")
            .select("*")
            .order("created_at", { ascending: false }),
          supabase.from("issue_reports").select("model_id"),
        ]);

        const { data: feed, error: feedError } = feedResult;
        const { data: reports, error: reportsError } = reportsResult;

        if (feedError) {
          console.error("Error fetching dashboard feed:", feedError);
          // If error but mock enabled, continue to show mock
          if (!MOCK_DATA_ENABLED) return;
        }

        let allPosts = feed || [];

        // Add mock data if enabled
        if (MOCK_DATA_ENABLED) {
          const mockPosts = generateMockData();
          allPosts = [...mockPosts, ...allPosts];
          // Sort combined list by date descending
          allPosts.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        }

        // Simple mapping - data already joined from view
        const mapped = allPosts
          .filter((item) => !!item.file_path)
          .map((item) => {
            const createdAt = new Date(item.created_at);

            // Determine author name
            let authorName = "Anonymous";
            if (!item.is_anonymous) {
              if (item.author) {
                authorName = item.author; // For mock data
              } else if (item.user_id) {
                authorName =
                  item.full_name ||
                  item.username ||
                  `User ${item.user_id.substring(0, 6)}`;
              }
            }

            return {
              title: item.report_title || item.title, // Handle different field names if any
              author: authorName,
              createdAt,
              date: dateFormatter.format(createdAt),
              category: (
                item.type_of_report ??
                item.category ??
                "uncategorized"
              ).trim(),
              filePath: item.file_path,
              providerName: item.org_name,
              summary: item.summary,
            };
          });

        // Process Stats
        let stats = [];
        if (!reportsError && reports) {
          const counts = {};
          reports.forEach((r) => {
            if (r.model_id) counts[r.model_id] = (counts[r.model_id] || 0) + 1;
          });

          const sortedIds = Object.keys(counts)
            .sort((a, b) => counts[b] - counts[a])
            .slice(0, 5);

          if (sortedIds.length > 0) {
            const { data: names } = await supabase
              .from("models")
              .select("id, name")
              .in("id", sortedIds);

            if (names) {
              stats = sortedIds.map((id) => {
                const m = names.find((n) => n.id === id);
                return { name: m ? m.name : "Unknown", count: counts[id] };
              });
            }
          }
        }

        dashboardCache.posts = mapped;
        dashboardCache.stats = stats;
        dashboardCache.lastFetch = Date.now();

        const recent = mapped.slice(0, 5);

        if (!alive) return;
        setTrendingTopics([...new Set(recent.map((p) => p.category))]);
        setPosts(mapped);
        setModelStats(stats);
        setLoading(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    };

    if (!hasFetchedRef.current || !dashboardCache.posts) {
      fetchPosts();
      hasFetchedRef.current = true;
    } else {
      fetchPosts(false);
    }

    const subscription = supabase
      .channel("dashboard-posts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "issue_reports" },
        () => fetchPosts(true)
      )
      .subscribe();

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, [dateFormatter]);

  const openViewer = async (filePath) => {
    try {
      setLoadingDoc(true);
      const publicUrl = resolvePublicUrl(filePath);
      if (publicUrl) {
        setPdfUrl(publicUrl);
        setViewerOpen(true);
        return;
      }

      const normalized = normalizeStoragePath(filePath);
      const { data: signed, error: signedErr } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(normalized, 60);

      if (signedErr || !signed?.signedUrl) {
        console.error("Could not resolve file URL:", signedErr);
        return;
      }

      setPdfUrl(signed.signedUrl);
      setViewerOpen(true);
    } catch (e) {
      console.error("Failed to open document:", e);
    } finally {
      setLoadingDoc(false);
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setPdfUrl(null);
  };

  const typeCounts = useMemo(() => {
    if (!Array.isArray(posts) || posts.length === 0) return {};
    return posts.reduce((acc, { category }) => {
      const key = (category ?? "uncategorized").trim().toLowerCase();
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }, [posts]);

  // Unique categories for filter chips
  const categories = useMemo(() => {
    const cats = new Set(
      posts.map((p) => (p.category ?? "uncategorized").trim())
    );
    return ["All", ...Array.from(cats)];
  }, [posts]);

  // Unique providers for filter chips
  const providers = useMemo(() => {
    const provs = new Set(
      posts.map((p) => (p.providerName ?? "Unknown").trim())
    );
    return ["All", ...Array.from(provs)];
  }, [posts]);

  // Filter posts based on selection
  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      const categoryMatch =
        selectedCategory === "All" ||
        (p.category ?? "uncategorized").trim() === selectedCategory;
      const providerMatch =
        selectedProvider === "All" ||
        (p.providerName ?? "Unknown").trim() === selectedProvider;

      const dateMatch = (() => {
        if (!startDate && !endDate) return true;
        const postDate = dayjs(p.createdAt);
        const start = startDate ? startDate.startOf("day") : null;
        const end = endDate ? endDate.endOf("day") : null;

        if (start && postDate.isBefore(start)) return false;
        if (end && postDate.isAfter(end)) return false;
        return true;
      })();

      return categoryMatch && providerMatch && dateMatch;
    });
  }, [posts, selectedCategory, selectedProvider, startDate, endDate]);

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render content within the Layout's 'main' area
  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      {/* Feed Section */}
      <Box component="div" sx={{ flexGrow: 1, p: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography
            variant="h4"
            sx={{ color: "text.primary", fontWeight: "bold" }}
          >
            Feed
          </Typography>
          <PostComponent isGuest={isGuest} />
        </Box>

        {/* Compact Filter Bar */}
        <Stack direction="row" spacing={2} sx={{ mb: 4, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="provider-select-label">Provider</InputLabel>
            <Select
              labelId="provider-select-label"
              id="provider-select"
              value={selectedProvider}
              label="Provider"
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              {providers.map((prov) => (
                <MenuItem key={prov} value={prov}>
                  {prov}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                },
                field: { clearable: true },
              }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>
        </Stack>

        <Grid container columnSpacing={0} rowSpacing={3} alignItems="stretch">
          {filteredPosts.map((post, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={`${post.filePath}-${index}`}
              sx={{ display: "flex", width: "100%" }}
            >
              <PostCard
                post={post}
                onOpenViewer={openViewer}
                resolvePublicUrl={resolvePublicUrl}
                loadingDoc={loadingDoc}
              />
            </Grid>
          ))}
        </Grid>

        <Dialog
          open={viewerOpen}
          onClose={closeViewer}
          fullWidth
          maxWidth="lg"
          scroll="paper"
        >
          <DialogContent dividers sx={{ p: 0 }}>
            {pdfUrl && (
              <iframe
                title="Embedded Content"
                src={pdfUrl}
                style={{
                  width: "100%",
                  height: "calc(90vh - 64px)",
                  border: "none",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>

      {/* Right Section */}
      <Box
        sx={{
          flexBasis: "20%",
          maxWidth: 300,
          minWidth: 220,
          p: 3,
          borderLeft: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          minHeight: "100vh",
          height: "auto",
          overflowY: "visible",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <TrendingUp sx={{ color: "secondary.main" }} />
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            Trending
          </Typography>
        </Stack>
        <List>
          {trendingTopics.map((topic, idx) => (
            <ListItemButton
              key={idx}
              sx={{
                borderRadius: 2,
                bgcolor:
                  selectedCategory === topic
                    ? "action.selected"
                    : "transparent",
              }}
              onClick={() => setSelectedCategory(topic)}
            >
              <ListItemText
                primary={topic}
                primaryTypographyProps={{
                  color:
                    selectedCategory === topic
                      ? "primary.main"
                      : "text.secondary",
                  fontWeight: selectedCategory === topic ? 600 : 400,
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <GppBad sx={{ color: "error.main" }} />
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            Most Attacked
          </Typography>
        </Stack>
        <List>
          {modelStats.map((stat, idx) => (
            <ListItemButton
              key={idx}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: "default",
                "&:hover": { bgcolor: "transparent" },
              }}
              disableRipple
            >
              <ListItemText
                primary={stat.name}
                secondary={`${stat.count} reports`}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              />
            </ListItemButton>
          ))}
          {modelStats.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              No data available
            </Typography>
          )}
        </List>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <BarChart sx={{ color: "info.main" }} />
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            Platform Stats
          </Typography>
        </Stack>
        <Stack
          spacing={2}
          sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}
        >
          <Box>
            <Typography variant="h5" color="primary.main" fontWeight="bold">
              {posts.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Posts
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              By Type
            </Typography>
            {Object.entries(typeCounts).map(([type, count]) => (
              <Box
                key={type}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {type}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {count}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

const DashboardPage = () => {
  return (
    <DashboardLayout>
      <DashboardContent />
    </DashboardLayout>
  );
};

export default DashboardPage;
